import React, { useState, useRef, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useIntl, FormattedMessage } from 'react-intl'
import { makePostRequest } from '../../../../../_state/helpers/api'
import { configWithAuth } from '../../../../../_state/helpers'
import { playerMessages } from '../CapeeshPlayer.i18n.Messages'

const LINE_HEIGHT_PX = 32
const TOP_PADDING = LINE_HEIGHT_PX
const BOTTOM_PADDING = 16

const CEFR_EXPAND = 5

function getTextAreaMinHeight(cefrLevel) {
    let lines = 7
    if (cefrLevel >= 3) lines += 2
    if (cefrLevel >= 4) lines += 2
    if (cefrLevel >= CEFR_EXPAND) return null
    return lines * LINE_HEIGHT_PX + TOP_PADDING + BOTTOM_PADDING
}

const useStyles = makeStyles(() => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '8px 0',
    },
    paperWrapper: {
        position: 'relative',
        width: '100%',
        borderRadius: 6,
        backgroundColor: '#FFFEF9',
        boxShadow:
            '0 1px 3px rgba(0,0,0,0.08), 0 6px 24px rgba(0,0,0,0.06), inset 0 0 0 1px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    textArea: {
        width: '100%',
        padding: `${TOP_PADDING}px 28px ${BOTTOM_PADDING}px 28px`,
        fontFamily: "'Georgia', 'Times New Roman', serif",
        fontSize: 16,
        lineHeight: `${LINE_HEIGHT_PX}px`,
        color: '#2C2C2C',
        backgroundColor: 'transparent',
        border: 'none',
        outline: 'none',
        resize: 'vertical',
        boxSizing: 'border-box',
        backgroundImage:
            `repeating-linear-gradient(transparent, transparent ${LINE_HEIGHT_PX - 1}px, rgba(0,0,0,0.06) ${LINE_HEIGHT_PX - 1}px, rgba(0,0,0,0.06) ${LINE_HEIGHT_PX}px)`,
        backgroundSize: `100% ${LINE_HEIGHT_PX}px`,
        backgroundPositionY: `${LINE_HEIGHT_PX - 1}px`,
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.25)',
            fontStyle: 'italic',
        },
        '&:disabled': {
            opacity: 0.5,
        },
    },
    charCount: {
        position: 'absolute',
        top: 6,
        right: 12,
        fontFamily: 'Rubik',
        fontSize: 11,
        color: 'rgba(0, 0, 0, 0.35)',
        zIndex: 1,
        pointerEvents: 'none',
    },
    submitButton: {
        padding: '12px 40px',
        borderRadius: 24,
        border: 'none',
        background: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, opacity 200ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
            transform: 'none',
        },
    },
    feedbackCard: {
        width: '100%',
        padding: '20px 24px',
        borderRadius: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
    },
    scoreRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    scoreLabel: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    scoreValue: {
        fontFamily: 'Rubik',
        fontSize: 24,
        fontWeight: 700,
    },
    feedbackList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    feedbackItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        opacity: 0,
        transform: 'translateY(12px)',
        animation: '$fadeSlideIn 400ms ease forwards',
        padding: '8px 12px',
        borderRadius: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
    feedbackEmoji: {
        fontSize: 18,
        lineHeight: '24px',
        flexShrink: 0,
        marginTop: 1,
    },
    feedbackItemText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '160%',
        color: '#FFFFFF',
    },
    feedbackText: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '160%',
        color: '#FFFFFF',
    },
    cefrBadge: {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'Rubik',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '20px 0',
    },
    loadingText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    '@keyframes fadeSlideIn': {
        from: { opacity: 0, transform: 'translateY(12px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
}))

const BUTTONS_RESERVED_HEIGHT = 140

const POSITIVE_EMOJIS = ['\u{1F44D}', '\u{2B50}', '\u{1F389}', '\u{1F4AA}', '\u{2705}', '\u{1F31F}']

function pickPositiveEmoji(index) {
    return POSITIVE_EMOJIS[index % POSITIVE_EMOJIS.length]
}

const WritingAiEvaluationInteraction = ({ testOption, onComplete, nodeId, customLessonId, cefrLevel = 3, isExamMode, l1Id, textAlign = 'center', hideSubmitButton, submitRef, onTextChange }) => {
    const classes = useStyles()
    const intl = useIntl()
    const [userText, setUserText] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [evaluation, setEvaluation] = useState(null)
    const [error, setError] = useState(null)
    const hasCompletedRef = useRef(false)

    const isFullScreen = cefrLevel >= CEFR_EXPAND
    const fixedMinHeight = getTextAreaMinHeight(cefrLevel)

    const handleSubmit = useCallback(async () => {
        if (!userText.trim() || isSubmitting) return

        setIsSubmitting(true)
        setError(null)

        try {
            const response = await makePostRequest(
                `ai/evaluation/lesson/${customLessonId}/writing`,
                JSON.stringify({
                    scenarioNodeId: nodeId,
                    userAnswer: userText.trim(),
                    l1LanguageId: l1Id,
                }),
                configWithAuth()
            )

            const result = response.data
            setEvaluation(result)

            if (!hasCompletedRef.current) {
                hasCompletedRef.current = true
                const isCorrect = result.normalizedScore >= 0.5
                onComplete({
                    testOptionId: testOption.testOptionId,
                    answers: [
                        {
                            testOptionId: testOption.testOptionId,
                            answer: userText.trim(),
                            correct: isCorrect,
                            normalizedScore: result.normalizedScore,
                            failCount: 0,
                            wrongTryCount: isCorrect ? 0 : 1,
                        },
                    ],
                })
            }
        } catch (err) {
            console.error('Writing evaluation failed:', err)
            setError(intl.formatMessage(playerMessages.couldNotEvaluate))
        } finally {
            setIsSubmitting(false)
        }
    }, [userText, isSubmitting, customLessonId, nodeId, testOption.testOptionId, onComplete])

    useEffect(() => {
        if (submitRef) submitRef.current = handleSubmit
    }, [submitRef, handleSubmit])

    useEffect(() => {
        if (onTextChange) onTextChange(!!userText.trim() && !isSubmitting)
    }, [userText, isSubmitting, onTextChange])

    const scoreColor = (score) => {
        if (score >= 0.8) return '#4CAF50'
        if (score >= 0.6) return '#8BC34A'
        if (score >= 0.4) return '#FFC107'
        return '#FF5722'
    }

    const feedbackItems = evaluation?.feedbackItems || []

    return (
        <div
            className={classes.container}
            style={isFullScreen && !evaluation && !isSubmitting
                ? { height: `calc(100vh - ${BUTTONS_RESERVED_HEIGHT}px)` }
                : undefined
            }
        >
            {!evaluation && !isSubmitting && (
                <>
                    <div
                        className={classes.paperWrapper}
                        style={isFullScreen
                            ? { flex: 1, minHeight: 0 }
                            : undefined
                        }
                    >
                        <Typography className={classes.charCount}>
                            {intl.formatMessage(playerMessages.words, { count: userText.trim() ? userText.trim().split(/\s+/).length : 0 })}
                        </Typography>
                        <textarea
                            className={classes.textArea}
                            style={isFullScreen
                                ? { flex: 1, minHeight: 0, resize: 'none', textAlign }
                                : { minHeight: fixedMinHeight, textAlign }
                            }
                            value={userText}
                            onChange={(e) => setUserText(e.target.value)}
                            placeholder={intl.formatMessage(playerMessages.writeAnswerPlaceholder)}
                            disabled={isSubmitting}
                            autoFocus
                        />
                    </div>
                    {!hideSubmitButton && (
                        <button
                            className={classes.submitButton}
                            onClick={handleSubmit}
                            disabled={!userText.trim() || isSubmitting}>
                            <FormattedMessage {...playerMessages.submit} />
                        </button>
                    )}
                    {error && (
                        <Typography style={{ color: '#FF5722', fontSize: 14, fontFamily: 'Rubik' }}>
                            {error}
                        </Typography>
                    )}
                </>
            )}

            {isSubmitting && (
                <div className={classes.loadingContainer}>
                    <CircularProgress size={36} style={{ color: 'rgba(255,255,255,0.7)' }} />
                    <Typography className={classes.loadingText}><FormattedMessage {...playerMessages.evaluatingAnswer} /></Typography>
                </div>
            )}

            {evaluation && !isExamMode && (
                <div className={classes.feedbackCard}>
                    <div className={classes.scoreRow}>
                        <Typography className={classes.scoreLabel}><FormattedMessage {...playerMessages.score} /></Typography>
                        <Typography
                            className={classes.scoreValue}
                            style={{ color: scoreColor(evaluation.normalizedScore) }}>
                            {Math.round(evaluation.normalizedScore * 100)}%
                        </Typography>
                    </div>

                    {feedbackItems.length > 0 ? (
                        <ul className={classes.feedbackList}>
                            {feedbackItems.map((item, idx) => (
                                <li
                                    key={idx}
                                    className={classes.feedbackItem}
                                    style={{ animationDelay: `${idx * 250}ms` }}
                                >
                                    <span className={classes.feedbackEmoji}>
                                        {item.isPositive ? pickPositiveEmoji(idx) : '\u{1F4DD}'}
                                    </span>
                                    <Typography className={classes.feedbackItemText}>
                                        {item.text}
                                    </Typography>
                                </li>
                            ))}
                        </ul>
                    ) : evaluation.feedback ? (
                        <Typography className={classes.feedbackText}>
                            {evaluation.feedback}
                        </Typography>
                    ) : null}

                    <div>
                        <span
                            className={classes.cefrBadge}
                            style={{
                                backgroundColor: evaluation.cefrAppropriate
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : 'rgba(255, 87, 34, 0.2)',
                                color: evaluation.cefrAppropriate ? '#4CAF50' : '#FF5722',
                            }}>
                            {evaluation.cefrAppropriate
                                ? intl.formatMessage(playerMessages.cefrLevelAppropriate)
                                : intl.formatMessage(playerMessages.belowCefrLevel)}
                        </span>
                    </div>
                </div>
            )}

            {evaluation && isExamMode && (
                <div className={classes.feedbackCard}>
                    <Typography className={classes.feedbackText} style={{ textAlign: 'center' }}>
                        <FormattedMessage {...playerMessages.answerSubmitted} />
                    </Typography>
                </div>
            )}
        </div>
    )
}

export default WritingAiEvaluationInteraction
